// GithubFilePicker - Custom Backstage Scaffolder Field Extension
//
// A dropdown picker that lists files from a GitHub repository matching
// a specified pattern, with optional filtering by Terraform module source.
//
// ## Basic Usage
//
// ```yaml
// ui:field: GithubFilePicker
// ui:options:
//   owner: my-org                      # GitHub organization
//   repoParam: selected_repo           # Form field containing repo name
//   path: terraform/tenants            # Directory path in the repo
//   pattern: 'tenant_*.tf'             # Glob pattern to filter files
// ```
//
// ## Advanced Usage - Deep Pattern Matching with Module Filtering
//
// For templates like `edit-business-unit` that need to find files across
// multiple directories and filter by Terraform module content:
//
// ```yaml
// ui:field: GithubFilePicker
// ui:options:
//   owner: my-org
//   repo: infrastructure-repo
//   pattern: "*/shared/main.tf"        # Deep pattern - searches subdirectories
//   terraformModuleSource: registry.terraform.io/hashicorp/consul/aws
//   displayRegex: "^([^/]+)"           # Show first path segment only
//   valueRegex: "^([^/]+)"             # Return first path segment as value
// ```
//
// ## Configuration Options
//
// | Option | Type | Description |
// |--------|------|-------------|
// | owner | string | GitHub organization (default: from config) |
// | repo | string | Direct repository name |
// | repoParam | string | Form field containing repository name |
// | branch | string | Git branch to fetch from (default: repo's default branch) |
// | path | string | Directory path prefix (for simple patterns) |
// | pattern | string | Glob pattern to match files (supports * and **) |
// | terraformModuleSource | string | Only show files containing this module source |
// | displayRegex | string | Regex with capture group to transform display text |
// | valueRegex | string | Regex with capture group to transform the returned value |
//
// ## Pattern Matching
//
// - Simple patterns (e.g., tenant_*.tf) search within the `path` directory
// - Deep patterns (e.g., */shared/main.tf) search from repository root
// - Use ** for recursive matching (e.g., **/main.tf)
//
// ## Display Transformation
//
// The `displayRegex` option extracts a portion of the file path for display.
// The first capture group is used as the display text.
//
// Example: For file `bu-platform/shared/main.tf` with displayRegex: '^([^/]+)',
// the dropdown shows `bu-platform` instead of the full path.

import React, { useEffect, useState, useCallback } from 'react';
import { useApi, githubAuthApiRef } from '@backstage/core-plugin-api';
import {
  FormControl,
  CircularProgress,
  TextField,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { FieldExtensionComponentProps } from '@backstage/plugin-scaffolder-react';

export interface GithubFilePickerOptions {
  /** GitHub organization name */
  owner?: string;
  /** Form field name containing the repository */
  repoParam?: string;
  /** Direct repository name (alternative to repoParam) */
  repo?: string;
  /** Git branch to fetch from (default: repo's default branch) */
  branch?: string;
  /** Directory path prefix for simple patterns */
  path?: string;
  /** Glob pattern to match files (supports *, **, ?) */
  pattern?: string;
  /** Only show files containing this Terraform module source */
  terraformModuleSource?: string;
  /** Regex with capture group to transform display text */
  displayRegex?: string;
  /** Regex with capture group to transform the returned value (defaults to full path) */
  valueRegex?: string;
  [key: string]: string | number | boolean | object | any[] | null | undefined;
}

interface FileEntry {
  name: string;
  path: string;
  type: 'file' | 'dir';
  sha: string;
}

interface DisplayableFile extends FileEntry {
  /** The display text (may be transformed by displayRegex) */
  displayName: string;
  /** Whether the file contains the required module source (if filtering) */
  hasModuleSource?: boolean;
}

export const GithubFilePicker = (
  props: FieldExtensionComponentProps<string, GithubFilePickerOptions>,
) => {
  const {
    onChange,
    rawErrors,
    required,
    formData,
    formContext,
    uiSchema,
    schema,
  } = props;

  const [files, setFiles] = useState<DisplayableFile[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const githubAuth = useApi(githubAuthApiRef);

  const owner = uiSchema?.['ui:options']?.owner || 'badal-io';
  const repoParam = uiSchema?.['ui:options']?.repoParam;
  const staticRepo = uiSchema?.['ui:options']?.repo;
  const branch = uiSchema?.['ui:options']?.branch as string | undefined;
  const path = uiSchema?.['ui:options']?.path || '';
  const pattern = uiSchema?.['ui:options']?.pattern || '*';
  const terraformModuleSource = uiSchema?.['ui:options']?.terraformModuleSource;
  const displayRegex = uiSchema?.['ui:options']?.displayRegex;
  const valueRegex = uiSchema?.['ui:options']?.valueRegex;

  // Get repo name from another form field or static config
  // Handle entity refs (e.g., "component:default/repo-name") by extracting just the name
  const rawRepoValue = repoParam
    ? (formContext?.formData as Record<string, unknown>)?.[repoParam] as string
    : staticRepo;

  // Extract name from entity ref format (kind:namespace/name) or use as-is
  const repoName = rawRepoValue?.includes('/')
    ? rawRepoValue.split('/').pop()
    : rawRepoValue;

  // Check if pattern requires deep search (contains path separators or **)
  const isDeepPattern = pattern.includes('/') || pattern.includes('**');

  // Convert glob pattern to regex for matching full paths
  const patternToRegex = useCallback((pat: string): RegExp => {
    let escaped = pat
      .replace(/[.+^${}()|[\]\\]/g, '\\$&')
      .replace(/\*\*/g, '{{GLOBSTAR}}')  // Temporarily replace **
      .replace(/\*/g, '[^/]*')            // * matches anything except /
      .replace(/\?/g, '.')
      .replace(/\{\{GLOBSTAR\}\}/g, '.*'); // ** matches anything including /
    return new RegExp(`^${escaped}$`);
  }, []);

  // Apply displayRegex to get display name
  const getDisplayName = useCallback((filePath: string): string => {
    if (!displayRegex) {
      return filePath;
    }
    try {
      const regex = new RegExp(displayRegex);
      const match = filePath.match(regex);
      if (match && match[1]) {
        return match[1]; // Return first capture group
      }
    } catch {
      console.warn(`[GithubFilePicker] Invalid displayRegex: ${displayRegex}`);
    }
    return filePath;
  }, [displayRegex]);

  // Apply valueRegex to transform the returned value
  const getValue = useCallback((filePath: string): string => {
    if (!valueRegex) {
      return filePath;
    }
    try {
      const regex = new RegExp(valueRegex);
      const match = filePath.match(regex);
      if (match && match[1]) {
        return match[1]; // Return first capture group
      }
    } catch {
      console.warn(`[GithubFilePicker] Invalid valueRegex: ${valueRegex}`);
    }
    return filePath;
  }, [valueRegex]);

  // Build GitHub Search API query from pattern and module source
  const buildSearchQuery = useCallback((): string => {
    const queryParts: string[] = [`repo:${owner}/${repoName}`];

    // Extract filename from pattern for search
    // Pattern like "*/shared/main.tf" -> filename:main.tf + path filter
    const patternParts = pattern.split('/');
    const filename = patternParts[patternParts.length - 1];

    // Add filename filter if it's a specific file (not just *)
    if (filename && filename !== '*' && !filename.includes('**')) {
      // Convert glob to search: tenant_*.tf -> "tenant_" extension:tf
      if (filename.includes('*')) {
        const ext = filename.split('.').pop();
        if (ext && ext !== '*') {
          queryParts.push(`extension:${ext}`);
        }
        // Add prefix as content search if there's a prefix before *
        const prefix = filename.split('*')[0];
        if (prefix && prefix.length > 2) {
          queryParts.push(`filename:${prefix}`);
        }
      } else {
        queryParts.push(`filename:${filename}`);
      }
    }

    // Add path filter only for simple patterns without wildcards
    // GitHub's path: filter requires the path to START with the given string
    // For patterns like "*/shared/main.tf", we skip path filter and rely on regex filtering
    if (patternParts.length > 1) {
      // Check if pattern has wildcards in the directory part
      const dirParts = patternParts.slice(0, -1);
      const hasWildcardInPath = dirParts.some(part => part.includes('*'));

      if (!hasWildcardInPath) {
        // No wildcards in path, we can use GitHub's path filter
        const pathPart = dirParts.join('/');
        if (pathPart && pathPart.length > 1) {
          queryParts.push(`path:${pathPart}`);
        }
      }
      // If there are wildcards in path, skip the path filter - we'll filter with regex later
    } else if (path) {
      // If no path in pattern, use the path option
      queryParts.push(`path:${path}`);
    }

    // Add module source as content search - this is very efficient!
    if (terraformModuleSource) {
      // Search for the module source string in file content
      queryParts.push(`"${terraformModuleSource}"`);
    }

    return queryParts.join(' ');
  }, [owner, repoName, pattern, path, terraformModuleSource]);

  // Use GitHub Search API for efficient file discovery
  const searchFilesWithApi = useCallback(async (
    token: string,
  ): Promise<FileEntry[]> => {
    const query = buildSearchQuery();
    console.log('[GithubFilePicker] Search query:', query);

    const url = new URL('https://api.github.com/search/code');
    url.searchParams.set('q', query);
    url.searchParams.set('per_page', '100');

    const response = await fetch(url.toString(), {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/vnd.github.v3+json',
      },
    });

    if (!response.ok) {
      // Search API may fail for empty repos or rate limits
      // Fall back to null to trigger contents API fallback
      console.warn('[GithubFilePicker] Search API failed:', response.status, response.statusText);
      return [];
    }

    const data = await response.json();

    // Convert search results to FileEntry format
    return (data.items || []).map((item: { name: string; path: string; sha: string }) => ({
      name: item.name,
      path: item.path,
      type: 'file' as const,
      sha: item.sha,
    }));
  }, [buildSearchQuery]);

  // Fallback: fetch directory contents (for simple patterns without module source filter)
  const fetchDirectoryContents = useCallback(async (
    token: string,
    dirPath: string,
  ): Promise<FileEntry[]> => {
    const url = new URL(`https://api.github.com/repos/${owner}/${repoName}/contents/${dirPath}`);
    if (branch) {
      url.searchParams.set('ref', branch);
    }

    const response = await fetch(url.toString(), {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/vnd.github.v3+json',
      },
    });

    if (!response.ok) return [];

    const entries = await response.json();
    return Array.isArray(entries) ? entries : [];
  }, [owner, repoName, branch]);

  // Fetch all files from a branch using Git Trees API (supports branch parameter)
  const fetchFilesFromBranch = useCallback(async (
    token: string,
  ): Promise<FileEntry[]> => {
    const ref = branch || 'HEAD';
    const url = `https://api.github.com/repos/${owner}/${repoName}/git/trees/${ref}?recursive=1`;
    console.log('[GithubFilePicker] Fetching tree from branch:', ref);

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/vnd.github.v3+json',
      },
    });

    if (!response.ok) {
      console.warn('[GithubFilePicker] Git Trees API failed:', response.status, response.statusText);
      return [];
    }

    const data = await response.json();

    // Filter to only files (blobs), not directories (trees)
    return (data.tree || [])
      .filter((item: { type: string }) => item.type === 'blob')
      .map((item: { path: string; sha: string }) => ({
        name: item.path.split('/').pop() || item.path,
        path: item.path,
        type: 'file' as const,
        sha: item.sha,
      }));
  }, [owner, repoName, branch]);

  // Fetch file content to check for module source
  const fetchFileContent = useCallback(async (
    token: string,
    filePath: string,
  ): Promise<string> => {
    const ref = branch || 'HEAD';
    const url = `https://api.github.com/repos/${owner}/${repoName}/contents/${filePath}?ref=${ref}`;

    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: 'application/vnd.github.v3+json',
      },
    });

    if (!response.ok) return '';

    const data = await response.json();
    if (data.content && data.encoding === 'base64') {
      return atob(data.content);
    }
    return '';
  }, [owner, repoName, branch]);

  // Search files in a specific branch with pattern and optional content filter
  const searchFilesInBranch = useCallback(async (
    token: string,
    patternRegex: RegExp,
  ): Promise<FileEntry[]> => {
    // Get all files from the branch
    const allFiles = await fetchFilesFromBranch(token);

    // Filter by pattern
    let matchingFiles = allFiles.filter(file => patternRegex.test(file.path));
    console.log('[GithubFilePicker] Files matching pattern:', matchingFiles.length);

    // If we need to filter by content (terraformModuleSource)
    if (terraformModuleSource && matchingFiles.length > 0) {
      console.log('[GithubFilePicker] Filtering by module source:', terraformModuleSource);
      const filesWithContent: FileEntry[] = [];

      // Check each file for the module source (in parallel, max 5 concurrent)
      const batchSize = 5;
      for (let i = 0; i < matchingFiles.length; i += batchSize) {
        const batch = matchingFiles.slice(i, i + batchSize);
        const results = await Promise.all(
          batch.map(async (file) => {
            const content = await fetchFileContent(token, file.path);
            if (content.includes(terraformModuleSource)) {
              return file;
            }
            return null;
          })
        );
        filesWithContent.push(...results.filter((f): f is FileEntry => f !== null));
      }
      matchingFiles = filesWithContent;
      console.log('[GithubFilePicker] Files with module source:', matchingFiles.length);
    }

    return matchingFiles;
  }, [fetchFilesFromBranch, fetchFileContent, terraformModuleSource]);

  useEffect(() => {
    const fetchFiles = async () => {
      if (!repoName) {
        setFiles([]);
        setError(null);
        return;
      }

      setLoading(true);
      setError(null);
      setLoadingStatus('Searching files...');

      try {
        const token = await githubAuth.getAccessToken(['repo']);
        const regex = patternToRegex(pattern);
        let matchingFiles: FileEntry[] = [];

        // Determine search strategy:
        // - If branch is specified, use Git Trees API (Search API only works on default branch)
        // - Otherwise, use Search API for efficiency
        const needsBranchSearch = Boolean(branch);
        const useSearchApi = !needsBranchSearch && (isDeepPattern || terraformModuleSource);

        if (needsBranchSearch && (isDeepPattern || terraformModuleSource)) {
          // Use Git Trees API for branch-specific searches
          setLoadingStatus(`Searching branch '${branch}'...`);
          matchingFiles = await searchFilesInBranch(token, regex);
        } else if (useSearchApi) {
          // Use Search API for default branch (more efficient)
          setLoadingStatus('Searching repository...');
          const searchResults = await searchFilesWithApi(token);

          if (searchResults.length > 0) {
            // Filter results by full pattern regex (Search API is approximate)
            matchingFiles = searchResults.filter(entry => {
              const pathToMatch = path ? entry.path.replace(`${path}/`, '') : entry.path;
              return regex.test(pathToMatch);
            });
          } else if (!terraformModuleSource) {
            // Search returned nothing and no content filter, try contents API fallback
            console.log('[GithubFilePicker] Search returned no results, falling back to contents API');
            setLoadingStatus('Fetching directory contents...');
            const allFiles = await fetchDirectoryContents(token, path || '');
            matchingFiles = allFiles
              .filter(entry => entry.type === 'file')
              .filter(entry => regex.test(entry.name));
          }
          // If terraformModuleSource is set and search returned nothing, that's the correct result
        } else {
          // Simple pattern in single directory - use contents API directly
          setLoadingStatus('Fetching files...');
          const allFiles = await fetchDirectoryContents(token, path || '');

          if (allFiles.length === 0) {
            setError(`Directory '${path}' not found or empty${branch ? ` (branch: ${branch})` : ''}`);
            setFiles([]);
            return;
          }

          matchingFiles = allFiles
            .filter(entry => entry.type === 'file')
            .filter(entry => regex.test(entry.name));
        }

        // Apply display transformation
        const displayableFiles: DisplayableFile[] = matchingFiles.map(file => ({
          ...file,
          displayName: getDisplayName(file.path),
        }));

        setFiles(displayableFiles);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch files');
      } finally {
        setLoading(false);
        setLoadingStatus('');
      }
    };

    fetchFiles();
  }, [
    githubAuth,
    owner,
    repoName,
    branch,
    path,
    pattern,
    patternToRegex,
    isDeepPattern,
    terraformModuleSource,
    searchFilesWithApi,
    searchFilesInBranch,
    fetchDirectoryContents,
    getDisplayName,
  ]);

  const handleChange = (
    _event: React.ChangeEvent<{}>,
    value: DisplayableFile | null,
  ) => {
    // Return the transformed value (using valueRegex if set) or full path
    onChange(value ? getValue(value.path) : '');
  };

  // Match by transformed value when valueRegex is set, otherwise by path
  const selectedFile = files.find(f => getValue(f.path) === formData) || null;

  // Show message if no repo selected
  if (!repoName) {
    return (
      <FormControl fullWidth margin="normal">
        <TextField
          label={schema.title || 'File'}
          disabled
          value=""
          helperText="Please select a repository first"
          variant="outlined"
        />
      </FormControl>
    );
  }

  if (loading) {
    return (
      <FormControl fullWidth margin="normal">
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <CircularProgress size={20} />
          <span>{loadingStatus || `Loading files from ${repoName}...`}</span>
        </div>
      </FormControl>
    );
  }

  if (error) {
    return (
      <FormControl fullWidth margin="normal" error>
        <TextField
          label={schema.title || 'File'}
          disabled
          value=""
          helperText={error}
          error
          variant="outlined"
        />
      </FormControl>
    );
  }

  if (files.length === 0) {
    const locationInfo = path
      ? `${owner}/${repoName}/${path}`
      : `${owner}/${repoName}`;
    return (
      <FormControl fullWidth margin="normal">
        <TextField
          label={schema.title || 'File'}
          disabled
          value=""
          helperText={`No files matching '${pattern}' found in ${locationInfo}${branch ? ` (branch: ${branch})` : ''}`}
          variant="outlined"
        />
      </FormControl>
    );
  }

  return (
    <FormControl
      fullWidth
      margin="normal"
      required={required}
      error={rawErrors && rawErrors.length > 0}
    >
      <Autocomplete
        id="github-file-picker"
        options={files}
        getOptionLabel={(option) => option.displayName}
        getOptionSelected={(option, value) => option.path === value.path}
        value={selectedFile}
        onChange={handleChange}
        renderInput={(params) => (
          <TextField
            {...params}
            label={schema.title || 'File'}
            required={required}
            helperText={
              (rawErrors && rawErrors.length > 0 && rawErrors.join(', ')) ||
              schema.description ||
              uiSchema?.['ui:help']
            }
            error={rawErrors && rawErrors.length > 0}
            variant="outlined"
          />
        )}
        renderOption={(option) => (
          <div>
            <div style={{ fontWeight: 500 }}>{option.displayName}</div>
            {option.displayName !== option.path && (
              <div style={{ fontSize: '0.75rem', color: '#888' }}>
                {option.path}
              </div>
            )}
          </div>
        )}
      />
    </FormControl>
  );
};

export default GithubFilePicker;

